import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

/** Session cache shared across Marketplace / Library / Dashboard. */
const taskThumbCache = new Map<string, string>();

function taskThumbnailFromDoc(data: any): string {
  const sample = typeof data?.sampleImageUrl === 'string' ? data.sampleImageUrl.trim() : '';
  if (sample) return sample;
  const icon = typeof data?.icon === 'string' ? data.icon.trim() : '';
  return icon || '';
}

async function fetchMissingTaskThumbs(taskIds: string[]): Promise<void> {
  const missing = Array.from(new Set(taskIds.filter((id) => id && !taskThumbCache.has(id))));
  const CHUNK = 40;
  for (let i = 0; i < missing.length; i += CHUNK) {
    const chunk = missing.slice(i, i + CHUNK);
    // eslint-disable-next-line no-await-in-loop
    const snaps = await Promise.all(chunk.map((id) => getDoc(doc(db, 'tasks', id))));
    snaps.forEach((snap, idx) => {
      const id = chunk[idx];
      if (!snap.exists()) {
        taskThumbCache.set(id, '');
        return;
      }
      taskThumbCache.set(id, taskThumbnailFromDoc(snap.data()));
    });
  }
}

/**
 * Resolve tasks/{taskId}.sampleImageUrl || icon for a list of task ids.
 * Returns a map of taskId → url (empty string if missing/failed).
 */
export function useTaskThumbnails(taskIds: string[]): Record<string, string> {
  const stableKey = useMemo(() => {
    const ids = Array.from(new Set(taskIds.filter((id) => typeof id === 'string' && id.trim())))
      .map((id) => id.trim())
      .sort();
    return ids.join('|');
  }, [taskIds]);

  const ids = useMemo(
    () => (stableKey ? stableKey.split('|') : []),
    [stableKey]
  );

  const [map, setMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (ids.length === 0) {
      setMap({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await fetchMissingTaskThumbs(ids);
      } catch (e) {
        console.warn('Failed to load task thumbnails', e);
      }
      if (cancelled) return;
      const next: Record<string, string> = {};
      ids.forEach((id) => {
        next[id] = taskThumbCache.get(id) || '';
      });
      setMap(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [stableKey, ids]);

  return map;
}
